import { useMutation } from '@apollo/client/react';
import { useTranslations } from '@vocab/react';
import { Box, Card, Heading, Loader, Stack } from 'braid-design-system';
import _ from 'lodash';
import { type FC, useEffect, useState } from 'react';

import type {
  UpdatePersonalDetailsMutation,
  UpdatePersonalDetailsMutationVariables,
} from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import {
  ActionOrigin,
  Page,
  PageSection,
  PageSectionState,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { usePersonalDetails } from 'src/context/usePersonalDetails';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { ConfirmableDrawer } from 'src/ui/ConfirmableDrawer/ConfirmableDrawer';

import { useProfileAnalytic } from '../../hooks/useProfileAnalytic';

import translations from './PersonalDetails.vocab';
import PersonalDetailsForm from './PersonalDetailsForm';
import PersonalDetailsView from './PersonalDetailsView';
import { type DefaultValues, ErrorKey } from './types';
import { UPDATE_PERSONAL_DETAILS } from './update-personal-details.graphql';

interface Props {
  onEditToggle?: (state: boolean) => void;
  isEditing?: boolean;
  error?: ErrorKey;
}
const PersonalDetails: FC<Props> = ({
  onEditToggle,
  isEditing = false,
  error,
}) => {
  const { t } = useTranslations(translations);
  const { config } = useConfig();
  const trackingClient = useTrackingClient();
  const { data, loading, refetch: refetchUser } = usePersonalDetails();
  const email = data?.viewer?.emailAddress;
  const firstName = data?.viewer?.personalDetails.firstName || '';
  const lastName = data?.viewer?.personalDetails.lastName || '';
  const phoneNumber = data?.viewer?.personalDetails.phoneNumber || '';
  const countryCode = data?.viewer?.personalDetails.countryCallingCode;
  const homeLocationShortDesc =
    data?.viewer?.currentLocation2?.shortDescription || '';
  const verifiedIdentityName = data?.viewer?.verifiedIdentity?.name;
  const homeLocationDesc = data?.viewer?.currentLocation2?.description || '';
  const homeLocationId = data?.viewer?.currentLocation2?.id || '';
  const defaultValues: DefaultValues = {
    firstName,
    lastName,
    phoneNumber,
    countryCodeId: countryCode?.id || '',
    homeLocation: { text: homeLocationDesc, value: homeLocationId },
  };
  const locationType = data?.viewer?.currentLocation2?.kind;
  const locationCountry =
    data?.viewer?.currentLocation2?.countryCode?.toLowerCase();

  const hasMissingInfo =
    !firstName || !lastName || !phoneNumber || !homeLocationShortDesc;

  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();

  const {
    isOpen,
    open: openPersonalDetailsForm,
    close: closePersonalDetailsForm,
  } = useDialogRouterState('personal');

  useEffect(() => {
    if (isEditing) {
      trackingClient.trackLink({
        link: 'apply-personal-details-expand',
        eventName: 'profile_section_displayed',
        isExitLink: false,
        actionOrigin: ActionOrigin.APPLY,
        currentPage: Page.DOCUMENT_PAGE,
        currentPageSection: PageSection.PERSONAL_DETAILS,
        currentPageSectionState: PageSectionState.EDIT,
      });
    }
  }, [isEditing, trackingClient]);

  useEffect(() => {
    if (hasMissingInfo && !loading) {
      onEditToggle?.(true);
    }
  }, [hasMissingInfo, onEditToggle, loading]);

  const [updatePersonalDetails, { error: apolloError }] = useMutation<
    UpdatePersonalDetailsMutation,
    UpdatePersonalDetailsMutationVariables
  >(UPDATE_PERSONAL_DETAILS, { errorPolicy: 'all' });

  const errors = [apolloError && ErrorKey.NETWORK, error].filter(
    Boolean,
  ) as ErrorKey[];

  if (errors.length) {
    trackingClient.trackLink({
      link: 'profile-update-failed',
    });
  }

  const handleSubmit = async (formData: DefaultValues) => {
    const { countryCodeId, homeLocation, ...rest } = formData;

    const currentLocationInput = {
      currentLocation: {
        id: Number(homeLocation.value),
      },
    };

    const personalDetailsInput = {
      ...rest,
      countryCallingCode: {
        id: countryCodeId,
      },
    };

    await updatePersonalDetails({
      variables: {
        personalDetailsInput,
        currentLocationInput,
        zone: config.ZONE,
      },
    });

    if (isOpen) {
      closePersonalDetailsForm();
    }

    await refetchUser();
    onEditToggle?.(false);

    if (!_.isEqual(defaultValues, formData)) {
      const difference = _.filter(
        _.keys(defaultValues),
        (key: keyof DefaultValues) =>
          !_.isEqual(defaultValues[key], formData[key]),
      ) as string[];

      trackingClient.trackLink({
        link: 'profile_update_succeeded',
        personalDetailsSections: difference,
        actionOrigin: ActionOrigin.APPLY,
        currentPage: Page.DOCUMENT_PAGE,
        currentPageSection: PageSection.PERSONAL_DETAILS,
        updateType: defaultValues.firstName ? UpdateType.EDIT : UpdateType.ADD,
        profileVisibilityLevel,
        profileSharedStatus,
        profileApproachabilityStatus,
        locationType,
        locationCountry,
      });
    }
  };

  const handleEdit = () => {
    openPersonalDetailsForm();
    onEditToggle?.(true);
  };

  const handleCancel = () => {
    closePersonalDetailsForm();
    onEditToggle?.(false);
  };
  const [isFormDirty, setIsFormDirty] = useState(false);

  if (loading) {
    return <Loader />;
  }

  if (isEditing && hasMissingInfo) {
    return (
      <Card>
        <Stack space="large">
          <Box component="legend">
            <Heading level="3">{t('Personal details')}</Heading>
          </Box>
          <PersonalDetailsForm
            email={email}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onCancel={hasMissingInfo ? undefined : handleCancel}
            errors={errors}
          />
        </Stack>
      </Card>
    );
  }

  return (
    <>
      <PersonalDetailsView
        onEditClick={handleEdit}
        firstName={firstName}
        lastName={lastName}
        emailAddress={email}
        countryCallingCode={countryCode?.countryCallingCode || ''}
        phoneNumber={phoneNumber}
        homeLocationShortDesc={homeLocationShortDesc}
        verifiedIdentityName={verifiedIdentityName || ''}
      />
      <ConfirmableDrawer
        width="small"
        name="personal-details"
        title={t('Personal details')}
        open={isOpen}
        shouldConfirmBeforeClose={isFormDirty}
        onClose={handleCancel}
      >
        <PersonalDetailsForm
          email={email}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={hasMissingInfo ? undefined : handleCancel}
          errors={errors}
          onFormDirtyChange={setIsFormDirty}
          verifiedIdentityName={verifiedIdentityName || ''}
        />
      </ConfirmableDrawer>
    </>
  );
};

export default PersonalDetails;
